import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { PoComboFilter, PoComboOption } from '@po-ui/ng-components';

import { GitService } from '../../core/api/git.service';
import { GitProject } from '../../core/interfaces/git.interface';
import { getApiErrorMessage } from '../../core/utils/http-error-message.util';

@Injectable()
export class AdminGitProjectComboFilterService implements PoComboFilter {
  provider = '';
  notify: ((error: string) => void) | null = null;

  private readonly byName = new Map<string, GitProject>();

  constructor(private gitService: GitService) {}

  getFilteredData(params: { property?: string; value?: string }): Observable<PoComboOption[]> {
    const provider = this.provider.trim();
    if (!provider) { return of([]); }

    return this.gitService.listProjects(provider, params.value || '', 50).pipe(
      tap(projects => {
        this.byName.clear();
        projects.forEach(project => this.byName.set(project.name, project));
        this.notify?.('');
      }),
      map(projects => projects.map(project => ({
        label: project.name,
        value: project.name
      }))),
      catchError(error => {
        this.notify?.(getApiErrorMessage(error, 'Não foi possível listar os projetos do provedor.'));
        return of([]);
      })
    );
  }

  getObjectByValue(value: string | number): Observable<PoComboOption> {
    const name = String(value ?? '');
    const cached = this.byName.get(name);
    if (cached) { return of({ label: cached.name, value: cached.name }); }
    if (!this.provider.trim() || !name.trim()) { return of({ label: name, value }); }

    return this.gitService.listProjects(this.provider, name, 50).pipe(
      tap(projects => projects.forEach(project => this.byName.set(project.name, project))),
      map(projects => {
        const found = projects.find(project => project.name === name);
        return found ? { label: found.name, value: found.name } : { label: name, value };
      }),
      catchError(() => of({ label: name, value }))
    );
  }

  projectFor(value: string): GitProject | null {
    return this.byName.get(value) ?? null;
  }

  reset(): void {
    this.provider = '';
    this.byName.clear();
    this.notify?.('');
  }
}
